import type { Router } from "vue-router";

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, unknown>) => void;
    };
  }
}

type TrackingConfig = {
  websiteId: string;
  host?: string;
  domain?: string;
  name?: string;
};

const TRACKING_SCRIPT_URL = "/api/v1/t/s.js";
const TRACKING_CONFIG_URL = "/api/v1/t/config";

let scriptLoaded = false;
let scriptLoading = false;
let trackingConfig: TrackingConfig | null = null;
let trackingConfigPromise: Promise<TrackingConfig | null> | null = null;
let trackingConfigResolved = false;

function isEphemeralTunnelHost(hostname: string): boolean {
  return (
    hostname.endsWith(".trycloudflare.com") ||
    hostname.endsWith(".ngrok-free.app") ||
    hostname.endsWith(".ngrok.io")
  );
}

async function fetchTrackingConfig(): Promise<TrackingConfig | null> {
  if (typeof window === "undefined") {
    return null;
  }

  if (isEphemeralTunnelHost(window.location.hostname)) {
    return null;
  }

  const url = new URL(TRACKING_CONFIG_URL, window.location.origin);
  url.searchParams.set("host", window.location.origin);

  const response = await fetch(url.toString(), {
    headers: {
      Accept: "application/json",
    },
    credentials: "same-origin",
  });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as Partial<TrackingConfig>;

  if (!data.websiteId) {
    return null;
  }

  return {
    websiteId: data.websiteId,
    host: data.host,
    domain: data.domain,
    name: data.name,
  };
}

function resolveTrackingConfig(): Promise<TrackingConfig | null> {
  if (trackingConfigResolved) {
    return Promise.resolve(trackingConfig);
  }

  if (!trackingConfigPromise) {
    trackingConfigPromise = fetchTrackingConfig()
      .then((config) => {
        trackingConfig = config;
        trackingConfigResolved = true;
        return config;
      })
      .catch(() => {
        trackingConfigResolved = true;
        return null;
      })
      .finally(() => {
        trackingConfigPromise = null;
      });
  }

  return trackingConfigPromise;
}

function loadUmamiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      resolve();
      return;
    }

    if (scriptLoaded) {
      resolve();
      return;
    }

    if (scriptLoading) {
      const checkLoaded = window.setInterval(() => {
        if (scriptLoaded) {
          window.clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    scriptLoading = true;

    resolveTrackingConfig()
      .then((config) => {
        if (!config?.websiteId) {
          scriptLoading = false;
          reject(new Error("Analytics website not configured for this host"));
          return;
        }

        const existingScript = document.querySelector<HTMLScriptElement>(
          `script[src="${TRACKING_SCRIPT_URL}"]`,
        );

        if (existingScript) {
          scriptLoaded = true;
          scriptLoading = false;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.async = true;
        script.defer = true;
        script.src = TRACKING_SCRIPT_URL;
        script.setAttribute("data-website-id", config.websiteId);

        if (config.domain) {
          script.setAttribute("data-domains", config.domain);
        }

        script.onload = () => {
          scriptLoaded = true;
          scriptLoading = false;
          resolve();
        };

        script.onerror = (error) => {
          scriptLoading = false;
          reject(error);
        };

        document.head.appendChild(script);
      })
      .catch((error) => {
        scriptLoading = false;
        reject(error);
      });
  });
}

function trackEvent(
  eventName: string,
  eventData?: Record<string, unknown>,
): void {
  if (window.umami) {
    window.umami.track(eventName, eventData);
    return;
  }

  loadUmamiScript()
    .then(() => {
      window.umami?.track(eventName, eventData);
    })
    .catch(() => {
      // Analytics failures should never block the app.
    });
}

function installRouterTracking(router: Router): void {
  let isFirstNavigation = true;

  router.afterEach((to, from) => {
    if (isFirstNavigation) {
      isFirstNavigation = false;
      return;
    }

    trackEvent("pageview", {
      url: to.fullPath,
      referrer: from.fullPath !== "/" ? from.fullPath : document.referrer,
    });
  });
}

export function useTracer() {
  return {
    init: loadUmamiScript,
    installRouterTracking,
    track: trackEvent,
    trackPageView: (url?: string, referrer?: string) => {
      const eventData: Record<string, unknown> = {};

      if (url) {
        eventData.url = url;
      }

      if (referrer) {
        eventData.referrer = referrer;
      }

      trackEvent(
        "pageview",
        Object.keys(eventData).length > 0 ? eventData : undefined,
      );
    },
    isConfigured: true,
  };
}
